import { DestroyRef, inject, Injectable } from '@angular/core';
import { ZardDialogService } from '../components/ui/dialog/dialog.service';
import {
  UnsavedChangesDialogComponent,
  UnsavedChangesDialogData,
  UnsavedDecision,
} from './unsaved-changes-dialog.component';

/**
 * Offene Arbeit, die beim Verlassen der Ansicht nicht verloren gehen darf.
 *
 * Jede Ansicht mit einem Formular meldet sich mit diesem Bündel an; die Rückfrage
 * und der Weg zum Speichern gehören dann nicht mehr dem Modul.
 */
export interface UnsavedWork {
  /** Wie die Rückfrage die Arbeit nennt: „Die Ordnervorlage „Standard Anlage"". */
  label: () => string;
  /** Steht etwas Ungespeichertes an? */
  hasChanges: () => boolean;
  /** Was das Speichern gerade verhindert — leer heißt „geht". */
  blocked?: () => string | undefined;
  /** Speichert. `false` heißt „hat nicht geklappt": dann wird nicht weitergegangen. */
  save: () => Promise<boolean>;
  /** Beschriftung des Speichern-Knopfes in der Rückfrage. */
  saveLabel?: string;
}

/**
 * Der Verlassen-Schutz der ganzen Anwendung.
 *
 * Wer ein Formular offen hat, meldet es hier an (`watchUnsavedWork`); die Route
 * trägt dazu `canDeactivate: [unsavedChangesGuard]`. Beim Wegnavigieren — in ein
 * anderes Modul, auf einen anderen Datensatz derselben Liste oder zurück in die
 * Liste — fragt der Wächter zuerst hier nach.
 *
 * Der Bestand hat das nirgends: eine angefangene Ordnerstruktur war beim ersten
 * Klick daneben weg, ohne Nachfrage und ohne Meldung.
 */
@Injectable({ providedIn: 'root' })
export class UnsavedChangesService {

  private readonly _dialogService: ZardDialogService = inject(ZardDialogService);

  /** Ein Set und keine einzelne Anmeldung: Liste und Blatt können gleichzeitig leben. */
  private readonly _work = new Set<UnsavedWork>();

  constructor() {
    // Das Schließen des Fensters lässt sich nicht mit einem eigenen Dialog
    // abfangen — der Browser zeigt seine eigene Rückfrage, aber nur, wenn hier
    // widersprochen wird. Ohne `preventDefault` fragt Chrome gar nicht erst.
    window.addEventListener('beforeunload', event => {
      if (!this.hasChanges()) return;
      event.preventDefault();
      event.returnValue = '';
    });
  }

  public watch(work: UnsavedWork): void {
    this._work.add(work);
  }

  public release(work: UnsavedWork): void {
    this._work.delete(work);
  }

  public hasChanges(): boolean {
    return [...this._work].some(work => work.hasChanges());
  }

  /**
   * Fragt nach, sobald etwas offen ist. `true` heißt „weitergehen".
   *
   * Bei mehreren angemeldeten Ansichten wird eine nach der anderen gefragt;
   * sagt eine „Hierbleiben", bricht der ganze Vorgang ab.
   */
  public async confirm(): Promise<boolean> {
    for (const work of [...this._work]) {
      if (!await this.confirmWork(work)) return false;
    }

    return true;
  }

  /**
   * Dieselbe Rückfrage für ein einzelnes Bündel — für Sheets und Dialoge, die
   * ihr Formular selbst schließen, statt wegzunavigieren. `true` heißt „zu".
   */
  public async confirmWork(work: UnsavedWork): Promise<boolean> {
    if (!work.hasChanges()) return true;

    const decision = await this._ask(work);

    if (decision === 'cancel') return false;
    if (decision === 'discard') return true;
    return work.save();
  }

  private _ask(work: UnsavedWork): Promise<UnsavedDecision> {
    return new Promise<UnsavedDecision>(resolve => {
      let decided = false;

      const settle = (decision: UnsavedDecision): void => {
        if (decided) return;
        decided = true;
        resolve(decision);
      };

      this._dialogService.create<UnsavedChangesDialogComponent, UnsavedChangesDialogData>({
        zTitle: 'Änderungen noch nicht gespeichert',
        zDescription: `${work.label()} hat Änderungen, die noch nicht gespeichert sind.`,
        zContent: UnsavedChangesDialogComponent,
        zData: {
          onDecision: settle,
          saveLabel: work.saveLabel ?? 'Speichern und weiter',
          blocked: work.blocked?.(),
        },
        // Die Fußzeile bringt der Inhalt mit: drei Wege passen nicht in die von ZardUI.
        zHideFooter: true,
        zCustomClasses: 'sm:max-w-lg',
        // Das Kreuz meint dasselbe wie „Hierbleiben".
        zOnCancel: () => settle('cancel'),
      });
    });
  }
}

/**
 * Meldet offene Arbeit an und gibt sie beim Zerstören der Komponente wieder frei.
 * Muss im Einspritzkontext stehen (Feldinitialisierung oder Konstruktor).
 */
export function watchUnsavedWork(work: UnsavedWork): void {
  const service = inject(UnsavedChangesService);
  const destroyRef = inject(DestroyRef);

  service.watch(work);
  destroyRef.onDestroy(() => service.release(work));
}
